// Lightweight client-side data cache for prefetching.
// Stores in-flight promises and resolved values keyed by string.
// Used by route loaders to start fetches before pages mount, and by
// page components to consume the same in-flight promise/cached value.

type CacheEntry<T> = {
  promise: Promise<T>;
  value?: T;
  error?: unknown;
  resolvedAt?: number;
};

const cache = new Map<string, CacheEntry<any>>();

const DEFAULT_TTL_MS = 60_000; // 1 minute — keeps cache fresh while reusing across route mounts

export const cacheGet = <T>(
  key: string,
  fetcher: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<T> => {
  const existing = cache.get(key) as CacheEntry<T> | undefined;

  if (existing) {
    if (existing.resolvedAt !== undefined) {
      const age = Date.now() - existing.resolvedAt;
      if (age < ttlMs && existing.error === undefined) {
        return existing.promise;
      }
      // stale → fall through and refetch
    } else {
      // in-flight → reuse
      return existing.promise;
    }
  }

  const entry: CacheEntry<T> = {
    promise: fetcher().then(
      (value) => {
        entry.value = value;
        entry.resolvedAt = Date.now();
        return value;
      },
      (error) => {
        entry.error = error;
        entry.resolvedAt = Date.now();
        throw error;
      },
    ),
  };
  cache.set(key, entry);
  return entry.promise;
};

export const cachePeek = <T>(key: string): T | undefined => {
  const entry = cache.get(key) as CacheEntry<T> | undefined;
  return entry?.value;
};

export const invalidate = (key: string): void => {
  cache.delete(key);
};

export const invalidatePrefix = (prefix: string): void => {
  Array.from(cache.keys()).forEach((key) => {
    if (key.startsWith(prefix)) cache.delete(key);
  });
};

export const clearCache = (): void => {
  cache.clear();
};
